import {
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonToolbar,
} from '@ionic/react';
import { Formik } from 'formik';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissionsLazy } from '../../api/authorization.provider';
import {
  EventChannel,
  EventChannelObject,
  EventChannelType,
  ListEventChannelsDocument,
  useCreateEventChannelMutation,
  useRemoveEventChannelMutation,
  useUpdateEventChannelMutation,
} from '../../api/graphql/generated';
import { logger } from '../../util/logger';
import {
  LeftAlignedButtons,
  RightAlignedButtons,
} from '../button/aligned-buttons';
import { BaseButton } from '../button/base-button';
import { DeleteButton } from '../button/delete-button';
import { FormSubmitButton } from '../form/form-submit.button';
import { SaveStatusToast } from '../toast/toast.gql';
import { ContentWithFooter } from '../ui/content-with-footer';
import {
  EventIntegrationOption,
  EventIntegrationServiceDetails,
  EventIntegrationServiceType,
  EventIntegrationTestStatus,
} from './event-interfaces';
import {
  SlackIntegrationForm,
  slackIntegrationSchema,
  testSlackIntegration,
} from './slack-integration-form';

interface IntegrationFormProps {
  integration?: EventChannel;
  onUpdate?: (channel: EventChannel) => void;
}

const eventIntegrationServiceDetails: Record<
  EventIntegrationServiceType,
  EventIntegrationServiceDetails
> = {
  slack: {
    name: 'slack',
    label: 'Slack',
  },
};

const EventIntegrationForm = (props: IntegrationFormProps): ReactElement => {
  const { integration, onUpdate } = props;
  const { i18n } = useTranslation();

  const [createEventChannel, { loading: createLoading }] =
    useCreateEventChannelMutation({
      refetchQueries: [ListEventChannelsDocument],
    });
  const [updateEventChannel, { loading: updateLoading }] =
    useUpdateEventChannelMutation();
  const [removeEventChannel, { loading: removeLoading }] =
    useRemoveEventChannelMutation();

  const checkPermissions = usePermissionsLazy();
  const [integrationServiceType, setIntegrationServiceType] =
    useState<EventIntegrationServiceType>(
      integration?.config.settings.service ?? EventIntegrationServiceType.SLACK
    );
  const [testStatus, setTestStatus] = useState<EventIntegrationTestStatus>({
    error: false,
    loading: false,
  });

  const canSave = useMemo(() => {
    if (integration !== undefined) {
      return (
        checkPermissions<Partial<EventChannel>>('event_channel', integration)
          .update ?? false
      );
    } else {
      return (
        checkPermissions<Partial<EventChannel>>('event_channel', {
          builtin: false,
          config: {
            service: integrationServiceType,
          },
        }).create ?? false
      );
    }
  }, [checkPermissions, integration, integrationServiceType]);

  const canDelete = useMemo(() => {
    if (integration !== undefined) {
      return (
        checkPermissions<Partial<EventChannel>>('event_channel', integration)
          .delete ?? false
      );
    }

    return false;
  }, [checkPermissions, integration]);

  const readOnly = !canSave;

  const onSubmit = useCallback(
    async (formData: Partial<EventChannel>): Promise<void> => {
      const { __typename, ...rest } = formData;
      let savedChannel: EventChannel | undefined = undefined;
      if (integration === undefined) {
        const created = await createEventChannel({
          variables: {
            input: {
              ...rest,
              config: {
                ...rest.config,
                service: integrationServiceType,
              },
              supportedObjects: [EventChannelObject.MACHINE],
              type: EventChannelType.WEBHOOK,
            },
          },
        });

        savedChannel = created.data?.createEventChannel ?? undefined;
      } else {
        const updated = await updateEventChannel({
          variables: {
            input: {
              ...rest,
            },
          },
        });

        savedChannel = updated.data?.updateEventChannel ?? undefined;
      }

      if (typeof onUpdate === 'function' && savedChannel !== undefined) {
        onUpdate(savedChannel);
      }
    },
    [
      createEventChannel,
      integration,
      integrationServiceType,
      onUpdate,
      updateEventChannel,
    ]
  );

  const deleteChannel = useCallback(
    async (channel: EventChannel): Promise<void> => {
      const removed = await removeEventChannel({
        variables: {
          input: {
            id: channel.id,
          },
        },
        refetchQueries: [ListEventChannelsDocument],
      });

      const removedChannel = removed.data?.removeEventChannel;

      if (typeof onUpdate === 'function' && removedChannel !== undefined) {
        onUpdate(removedChannel);
      }
    },
    [onUpdate, removeEventChannel]
  );

  const restoreChannel = useCallback(
    async (channel: EventChannel) => {
      const { __typename, ...rest } = channel;
      await updateEventChannel({
        variables: {
          input: {
            ...rest,
            active: true,
          },
        },
      });
    },
    [updateEventChannel]
  );

  const deleteDisabled = updateLoading || removeLoading;
  const submitDisabled = createLoading || updateLoading;

  const {
    FormComponent,
    validationSchema,
    testIntegration,
  }: EventIntegrationOption = useMemo(() => {
    if (integrationServiceType === EventIntegrationServiceType.SLACK) {
      return {
        FormComponent: SlackIntegrationForm,
        validationSchema: slackIntegrationSchema,
        testIntegration: testSlackIntegration,
      };
    } else {
      logger.error('Form not implemented', {
        service: integrationServiceType,
      });
      return { FormComponent: null, validationSchema: undefined };
    }
  }, [integrationServiceType]);

  return (
    <Formik
      initialValues={{ ...integration }}
      onSubmit={(form) => {
        onSubmit(form);
      }}
      enableReinitialize={true}
      validateOnMount={true}
      validationSchema={validationSchema}
    >
      {({ values, isValid, dirty, submitForm }) => {
        let footer = <div />;
        if (!readOnly) {
          const showDeleteButton = canDelete && integration?.active === true;
          const showRestoreButton = canSave && integration?.active === false;
          const showSaveButton = canSave && (integration?.active ?? true);

          footer = (
            <IonToolbar>
              <div style={{ display: 'flex' }}>
                <LeftAlignedButtons>
                  {testIntegration && (
                    <BaseButton
                      buttonProps={{
                        onClick: async () => {
                          setTestStatus({
                            error: false,
                            loading: true,
                            successMessage: undefined,
                            errorMessage: undefined,
                          });
                          const results = await testIntegration(values);
                          if (results.success === true) {
                            setTestStatus({
                              error: false,
                              loading: false,
                              successMessage: results.successMessage,
                            });
                          } else {
                            setTestStatus({
                              error: true,
                              loading: false,
                              errorMessage: results.errorMessage,
                            });
                          }
                        },
                        color: 'primary',
                        fill: 'outline',
                        disabled: testStatus.loading || !isValid,
                      }}
                    >
                      {i18n.t('common:event_integration_test')}
                    </BaseButton>
                  )}
                </LeftAlignedButtons>
                <RightAlignedButtons>
                  {showDeleteButton && (
                    <DeleteButton
                      header={i18n.t(
                        'common:event_integration_delete_confirmation'
                      )}
                      onDelete={() => {
                        deleteChannel(integration);
                      }}
                      buttonProps={{
                        type: 'button',
                        disabled: deleteDisabled,
                      }}
                    >
                      {i18n.t('common:delete')}
                    </DeleteButton>
                  )}
                  {showRestoreButton && (
                    <BaseButton
                      buttonProps={{
                        onClick: () => restoreChannel(integration),
                      }}
                    >
                      {i18n.t('common:event_integration_restore')}
                    </BaseButton>
                  )}
                  {showSaveButton && (
                    <FormSubmitButton
                      placement="none"
                      disabled={submitDisabled || !dirty || !isValid}
                      label={i18n.t('common:save')}
                      buttonProps={{
                        onClick: submitForm,
                      }}
                    />
                  )}
                </RightAlignedButtons>
              </div>
            </IonToolbar>
          );
        }

        return (
          <ContentWithFooter footer={footer}>
            <>
              <>
                <IonItem lines="full">
                  <IonSelect
                    aria-label="service-type"
                    name="serviceType"
                    interface="popover"
                    onIonChange={(e) => {
                      setIntegrationServiceType(e.detail.value);
                    }}
                    value={integrationServiceType}
                  >
                    <div slot="label">
                      <IonLabel color="primary">
                        {i18n.t('common:event_integration_service')}
                      </IonLabel>
                    </div>
                    {Object.values(EventIntegrationServiceType)?.map(
                      (service) => {
                        const details = eventIntegrationServiceDetails[service];

                        return (
                          <IonSelectOption key={details.name} value={service}>
                            {details.label}
                          </IonSelectOption>
                        );
                      }
                    )}
                  </IonSelect>
                </IonItem>
                {FormComponent && (
                  <FormComponent
                    onDelete={deleteChannel}
                    onRestore={restoreChannel}
                    canDelete={canDelete}
                    canSubmit={canSave}
                    channel={integration}
                    deleteDisabled={deleteDisabled}
                    submitDisabled={submitDisabled || !dirty}
                    readOnly={readOnly}
                  />
                )}
              </>
              <SaveStatusToast loading={createLoading || updateLoading} />
              <SaveStatusToast
                loading={testStatus.loading}
                error={testStatus.error}
                defaultMessageKey={testStatus.successMessage}
                defaultErrorMessageKey={testStatus.errorMessage}
              />
            </>
          </ContentWithFooter>
        );
      }}
    </Formik>
  );
};

export { EventIntegrationForm };
